// What a slide or block selection is called once it reaches the Downloads
// folder.
//
// The file used to be named after the deck: every selection out of one deck
// arrived as "DSGN1140 - Week 1 - Traditional 2D Animation Expanded_v156_
// Slides.json", whatever was in it. Two exports from the same deck were
// indistinguishable without opening them, which is the one thing a filename is
// for.
//
// The name now says what is inside: the slide's number and name, and the block
// when a single block is what left. The number is the slide's place in the
// running deck — the same figure the deck counter, the outline drawer and the
// transfer window print — so a file can be matched back to a slide on sight.

/** Windows and macOS both refuse these; spaces become underscores like the rest. */
export const sanitizeNamePart = (value) => String(value == null ? '' : value)
  .trim()
  .replace(/[\\/:*?"<>|]+/g, '_')
  .replace(/\s+/g, '_')
  .replace(/_+/g, '_')
  .replace(/^_+|_+$/g, '');

/**
 * Long slide names are trimmed at a word boundary rather than mid-word.
 *
 * 48 characters keeps the whole filename — number, name, block, version and
 * extension — inside what a file listing shows without truncating, and a slide
 * called "A System for Planning and Timing Animation" survives whole.
 */
const MAX_LABEL = 48;

export const trimNamePart = (value, max = MAX_LABEL) => {
  const cleaned = sanitizeNamePart(value);
  if (cleaned.length <= max) return cleaned;
  const cut = cleaned.slice(0, max);
  const lastBreak = cut.lastIndexOf('_');
  return lastBreak > max * 0.5 ? cut.slice(0, lastBreak) : cut;
};

/** "Slide-23", or "Slide" when the deck position is not known. */
const slideToken = (number) => (Number.isFinite(number) && number > 0 ? `Slide-${number}` : 'Slide');

/**
 * A run of slides, named by their numbers.
 *
 * Up to four are listed; beyond that the list would be longer than the rest of
 * the name, so it becomes the span it covers. Both forms sort and read the way
 * the deck does.
 */
const slidesToken = (numbers) => {
  const known = numbers.filter((n) => Number.isFinite(n) && n > 0).sort((a, b) => a - b);
  if (!known.length) return 'Slides';
  if (known.length === 1) return slideToken(known[0]);
  if (known.length <= 4) return `Slides-${known.join('-')}`;
  return `Slides-${known[0]}-to-${known[known.length - 1]}`;
};

/**
 * The name for a slide selection.
 *
 * One slide is named after itself. Several are named by their numbers, because
 * no one of them is the subject and the numbers say which part of the deck
 * this file came from.
 */
export const buildSlideSelectionName = (slides = []) => {
  const entries = Array.isArray(slides) ? slides : [];
  if (!entries.length) return '';

  if (entries.length === 1) {
    const [slide] = entries;
    const label = trimNamePart(slide.title || slide.sectionTitle);
    return [slideToken(slide.deckNumber), label].filter(Boolean).join('_');
  }

  return slidesToken(entries.map((slide) => slide.deckNumber));
};

/**
 * The name for a block selection.
 *
 * A single block earns its own name — that is the case where the author picked
 * one card out of one slide and needs to recognise it later. More than one
 * block is named for the slide or slides they came from, plus how many, since
 * listing them would say less than the count does.
 */
export const buildBlockSelectionName = (blocks = []) => {
  const entries = Array.isArray(blocks) ? blocks : [];
  if (!entries.length) return '';

  if (entries.length === 1) {
    const [entry] = entries;
    const slideLabel = trimNamePart(entry.slideTitle || entry.sectionTitle);
    const blockNumber = Number.isFinite(entry.blockIndex) && entry.blockIndex > 0
      ? `Block-${entry.blockIndex}`
      : 'Block';
    const blockLabel = trimNamePart(
      entry.blockType || entry.blockLabel,
      24
    );
    return [slideToken(entry.deckNumber), slideLabel, blockNumber, blockLabel]
      .filter(Boolean)
      .join('_');
  }

  const slideNumbers = [...new Set(entries.map((entry) => entry.deckNumber).filter(Boolean))];
  const count = `${entries.length}-blocks`;

  if (slideNumbers.length === 1) {
    const [entry] = entries;
    const slideLabel = trimNamePart(entry.slideTitle || entry.sectionTitle);
    return [slideToken(entry.deckNumber), slideLabel, count].filter(Boolean).join('_');
  }

  return [slidesToken(slideNumbers), count].filter(Boolean).join('_');
};
